import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { WorkspaceActionMenu } from "@/components/workspace/workspace-action-menus";
import type { WorkspaceProject } from "@/components/workspace/workspace-types";
import { useRegisterCommand } from "@/lib/command-registry";
import { Cpu, MoreHorizontal } from "lucide-react";
/**
 * Project card — the visual element in the gallery view. Click navigates
 * to the project detail page; the right-click / overflow menu opens the
 * `WorkspaceActionMenu` (rename/move/delete).
 */
import { Link } from "react-router-dom";

export interface ProjectCardProps {
  project: WorkspaceProject;
  view: "gallery" | "list";
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function ProjectCard({ project, view }: ProjectCardProps): JSX.Element {
  useRegisterCommand({
    id: `project.open.${project.id}`,
    title: `Open "${project.name}"`,
    group: "Projects",
    keywords: [project.name, "open"],
    perform: () => {
      if (typeof window !== "undefined") window.location.assign(`/project/${project.id}`);
    },
  });

  if (view === "list") {
    return (
      <Link
        to={`/project/${project.id}`}
        className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2 transition-colors hover:bg-accent/40"
      >
        <Cpu className="h-4 w-4 text-muted-foreground" />
        <div className="flex-1 truncate">
          <div className="truncate text-sm font-medium">{project.name}</div>
          {project.description ? (
            <div className="truncate text-xs text-muted-foreground">{project.description}</div>
          ) : null}
        </div>
        <span className="text-xs text-muted-foreground">{timeAgo(project.updated_at)}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-8" aria-label="Project actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <WorkspaceActionMenu project={project} />
          </DropdownMenuContent>
        </DropdownMenu>
      </Link>
    );
  }

  return (
    <div className="group relative overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-sm transition-all hover:border-primary/40 hover:shadow-md">
      <Link to={`/project/${project.id}`} className="flex flex-col">
        <div className="flex h-32 items-center justify-center bg-gradient-to-br from-muted to-background">
          {project.thumbnail_url ? (
            <img
              src={project.thumbnail_url}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <Cpu className="h-12 w-12 text-muted-foreground/40" />
          )}
        </div>
        <div className="space-y-1.5 p-3">
          <div className="truncate text-sm font-medium">{project.name}</div>
          {project.description ? (
            <p className="line-clamp-2 text-xs text-muted-foreground">{project.description}</p>
          ) : (
            <p className="text-xs text-muted-foreground/70">No description.</p>
          )}
          <div className="pt-1 text-xs text-muted-foreground">{timeAgo(project.updated_at)}</div>
        </div>
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Project actions"
            className="absolute right-1 top-1 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <WorkspaceActionMenu project={project} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export { timeAgo };
